import type { IconValues } from '@gat-ship/icons'
import type { Component } from 'vue'

export const settingsNames = {
  agency: {
    client: {
      billing: {
        defaultCurrency: 'client.billing.invoicing.defaultCurrency',
        paymentTerms: 'client.billing.defaultPaymentTerms'
      }
    }
  },
  global: {
    TBA: 'global.TBA'
  },
  user: {
    TBA: 'user.TBA'
  }
} as const

export type AgencySettingName =
  (typeof settingsNames.agency.client.billing)[keyof typeof settingsNames.agency.client.billing]

export type GlobalSettingName =
  (typeof settingsNames.global)[keyof typeof settingsNames.global]

export type UserSettingName =
  (typeof settingsNames.user)[keyof typeof settingsNames.user]

export type SettingName = AgencySettingName
/* | GlobalSettingName
  | UserSettingName */

export const settingNamesMetadata: Record<
  SettingName,
  { title: string; description: string; icon: IconValues }
> = {
  'client.billing.invoicing.defaultCurrency': {
    title: 'Invoice Currency',
    description: 'The currency used for invoices.',
    icon: 'DollarSign'
  },
  'client.billing.defaultPaymentTerms': {
    title: 'Payment Terms',
    description: 'The payment terms for the agency.',
    icon: 'Clock'
  }
} as const

export type TypedSettingValues = {
  'client.billing.invoicing.defaultCurrency': string
  'client.billing.defaultPaymentTerms': {
    paymentPeriodInDays: number
    useEndOfMonthTerms: boolean
  }
}

// Fallback type for settings that don't have a specific type defined
export type UntypedSettingValue = null

export function isAgencySettingsCode(code: string): code is AgencySettingName {
  return Object.values(settingsNames.agency).some((code) => code === code)
}

export function isGlobalSettingsCode(code: string): code is GlobalSettingName {
  return Object.values(settingsNames.global).some((code) => code === code)
}

export function isUserSettingsCode(code: string): code is UserSettingName {
  return Object.values(settingsNames.user).some((code) => code === code)
}

export function isSettingsCode(code: string): code is SettingName {
  return Object.values(settingsNames).some((group) =>
    Object.values(group).some((code) => code === code)
  )
}

export function isStringArrayOfSettingsCodes(
  codes: string[]
): codes is SettingName[] {
  return codes.every((code) => isSettingsCode(code))
}

export interface EffectiveSettingDirect<TValue> {
  value: TValue
  valueStatus: 'direct'
  permissionsMissing: string[]
}

export interface EffectiveSettingInherited<TValue> {
  value: TValue
  valueStatus: 'inherited'
  permissionsMissing: string[]
}

export interface EffectiveSettingMissing {
  value: null
  valueStatus: 'missing'
  permissionsMissing: string[]
}

export interface EffectiveSettingForbidden {
  value: null
  valueStatus: 'forbidden'
  permissionsMissing: string[]
}

export type EffectiveSetting<TValue> =
  | EffectiveSettingDirect<TValue>
  | EffectiveSettingInherited<TValue>
  | EffectiveSettingMissing
  | EffectiveSettingForbidden

// Helper type to map a setting name to its EffectiveSetting type
export type MapSettingToEffectiveSetting<T extends SettingName> =
  T extends keyof TypedSettingValues
    ? EffectiveSetting<TypedSettingValues[T]>
    : EffectiveSetting<UntypedSettingValue>

// Type that takes a tuple of setting names and returns a mapped record
// where each setting name maps to its corresponding EffectiveSetting type
// Example: EffectiveSettings<["client.billing.defaultPaymentTerms", "client.billing.invoicing.defaultCurrency"]>
// returns: {
//   "client.billing.defaultPaymentTerms": EffectiveSetting<DefaultPaymentTermsValue>
//   "client.billing.invoicing.defaultCurrency": EffectiveSetting<DefaultCurrencyValue>
// }
export type EffectiveSettings<T extends readonly SettingName[]> = {
  [K in T[number]]: MapSettingToEffectiveSetting<K>
}

// Type that extracts just the value type for a single setting name
// Example: EffectiveSettingValue<'client.billing.defaultPaymentTerms'>
// returns: { paymentPeriodInDays: number; useEndOfMonthTerms: boolean }
export type EffectiveSettingValue<TSettingName extends SettingName> =
  TSettingName extends keyof TypedSettingValues
    ? TypedSettingValues[TSettingName]
    : UntypedSettingValue

export interface LayeredSetting<TValue> {
  layerScope: 'default' | 'agency' | 'global' | 'user'
  value: TValue
  scopeEntityId: string | null
  scopeEntityName: string | null
}

export interface ScopedSettingAssigned<TValue> {
  value: TValue
  layerStatus: 'assigned'
  permissionsMissing: string[]
  parentChain: LayeredSetting<TValue>[]
}

export interface ScopedSettingInherited<TValue> {
  value: null
  layerStatus: 'inherited'
  permissionsMissing: string[]
  parentChain: LayeredSetting<TValue>[]
}

export interface ScopedSettingForbidden {
  value: null
  layerStatus: 'forbidden'
  permissionsMissing: string[]
  parentChain: LayeredSetting<null>[]
}

export type ScopedSetting<TValue> =
  | ScopedSettingAssigned<TValue>
  | ScopedSettingInherited<TValue>
  | ScopedSettingForbidden

// Helper type to map a setting name to its EffectiveSetting type
export type MapSettingToScopedSetting<T extends SettingName> =
  T extends keyof TypedSettingValues
    ? ScopedSetting<TypedSettingValues[T]>
    : ScopedSetting<UntypedSettingValue>

// Type that takes a tuple of setting names and returns a mapped record
// where each setting name maps to its corresponding EffectiveSetting type
// Example: EffectiveSettings<["client.billing.defaultPaymentTerms", "client.billing.invoicing.defaultCurrency"]>
// returns: {
//   "client.billing.defaultPaymentTerms": EffectiveSetting<DefaultPaymentTermsValue>
//   "client.billing.invoicing.defaultCurrency": EffectiveSetting<DefaultCurrencyValue>
// }
export type ScopedSettings<T extends readonly SettingName[]> = {
  [K in T[number]]: MapSettingToScopedSetting<K>
}

// Type that extracts just the value type for a single setting name
// Example: ScopedSettingValue<'client.billing.defaultPaymentTerms'>
// returns: { paymentPeriodInDays: number; useEndOfMonthTerms: boolean }
export type ScopedSettingValue<TSettingName extends SettingName> =
  TSettingName extends keyof TypedSettingValues
    ? TypedSettingValues[TSettingName]
    : UntypedSettingValue

export type SettingMetadata<TSettingName extends SettingName> = {
  /** The title of the setting. */
  title: string
  /** The description of the setting. */
  description?: string
  /** The privilege level of the setting. */
  privilegeLevel?: 'high'
  /** The value of the setting to display in the list item. */
  listItemValueFn?: (value: EffectiveSettingValue<TSettingName>) => string
  editModal: {
    inputComponent: Component
  }
}

export interface SettingGroupMetadata {
  description?: string
  settingItems: SettingName[]
  icon: IconValues
}
